import { useMemo } from "react";
import { useSearch } from "@tanstack/react-router";
import { useAuth } from "./useAuth";
import {
  useUserAirlineGroupsQuery as useUserAirlineGroupsNonSuspenseQuery,
  useUserAirlineGroupsSuspenseQuery,
} from "./useUserAirlineGroupsQuery";
import { isFeatureRunning } from "../utils/features";
import { parseAirlineCode, parseAirlineCodes } from "../utils/selectedAirline";

type UseSelectedAirlineProps = {
  useSuspense?: boolean;
};

export const useSelectedAirline = ({ useSuspense = false }: UseSelectedAirlineProps = {}) => {
  const { isAirlineUser } = useAuth();
  const { airlineCode: airlineCodeFromUrl, airlineCodes: airlineCodesFromUrl } = useSearch({ strict: false }) as {
    airlineCode?: string;
    airlineCodes?: string;
  };

  const aggregatedPerformanceEnabled = isFeatureRunning(
    "FLIGHTS_PARTNER_STRATEGY_MANAGER_AGGREGATED_AIRLINE_PERFORMANCE"
  );

  const useUserAirlineGroupsQuery = useSuspense
    ? useUserAirlineGroupsSuspenseQuery
    : useUserAirlineGroupsNonSuspenseQuery;
  const { data: eligibleAirlineGroups } = useUserAirlineGroupsQuery();

  const isGroupAirlineUser = useMemo(
    () =>
      isFeatureRunning("FLIGHTS_PARTNER_STRATEGY_MANAGER_RESOLVE_SCOPE_FROM_DB") &&
      isAirlineUser &&
      !!eligibleAirlineGroups?.some(({ group, airlines }) => !!group && airlines.length > 1),
    [isAirlineUser, eligibleAirlineGroups]
  );

  const eligibleAirlines = useMemo(
    () => (eligibleAirlineGroups || []).flatMap(({ airlines }) => airlines),
    [eligibleAirlineGroups]
  );
  const eligibleAirlineCodes = useMemo(() => eligibleAirlines.map(({ code }) => code), [eligibleAirlines]);

  const selectedAirlineCodes = useMemo(() => {
    const selectedAirlineCodeFromUrl = parseAirlineCode(airlineCodeFromUrl);
    const selectedAirlineCodesFromUrl = parseAirlineCodes(airlineCodesFromUrl);
    const selectedAirlineCodes = aggregatedPerformanceEnabled
      ? selectedAirlineCodesFromUrl
      : selectedAirlineCodeFromUrl
        ? [selectedAirlineCodeFromUrl]
        : [];

    if (isAirlineUser && !selectedAirlineCodes.length) {
      return aggregatedPerformanceEnabled
        ? eligibleAirlineCodes
        : eligibleAirlineCodes.length
          ? [eligibleAirlineCodes[0]]
          : [];
    }

    return selectedAirlineCodes;
  }, [aggregatedPerformanceEnabled, airlineCodeFromUrl, airlineCodesFromUrl, isAirlineUser, eligibleAirlineCodes]);

  const selectedAirlines = useMemo(
    () => eligibleAirlines.filter(({ code }) => selectedAirlineCodes.includes(code)),
    [eligibleAirlines, selectedAirlineCodes]
  );

  const selectedAirline = useMemo(
    () => (selectedAirlines.length ? selectedAirlines[0] : undefined),
    [selectedAirlines]
  );

  return {
    selectedAirline,
    selectedAirlines,
    selectedAirlineCodes,
    isGroupAirlineUser,
  };
};
